import React, { useState, useEffect } from 'react';
import { useAuth } from '../Auth/AuthProvider';
import * as FiIcons from 'react-icons/fi';
import { getAuditLog } from '../../services/platformAdminService';

const {
  FiFileText,
  FiRefreshCw,
  FiUser,
  FiCalendar,
  FiActivity,
  FiChevronLeft,
  FiChevronRight
} = FiIcons;

const AuditLog = () => {
  const { hasSuperadminPermission } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 50;

  const fetchLogs = async (pageNum = 0) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAuditLog({ limit, offset: pageNum * limit });
      // Edge Function returns 'entries', not 'logs'
      setLogs(data.entries || []);
      setHasMore((data.entries || []).length === limit);
      setPage(pageNum);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasSuperadminPermission('audit:read')) {
      fetchLogs();
    }
  }, [hasSuperadminPermission]);

  if (!hasSuperadminPermission('audit:read')) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          You don't have permission to view the audit log.
        </div>
      </div>
    );
  }

  const getActionColor = (action) => {
    if (!action) return 'text-gray-600 bg-gray-50';
    const actionUpper = action.toUpperCase();
    if (actionUpper.includes('DELETE') || actionUpper.includes('REMOVE')) return 'text-red-600 bg-red-50';
    if (actionUpper.includes('CREATE') || actionUpper.includes('ADD') || actionUpper.includes('BOOTSTRAP')) return 'text-green-600 bg-green-50';
    if (actionUpper.includes('UPDATE') || actionUpper.includes('CHANGE')) return 'text-blue-600 bg-blue-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'tenant': return '🏢';
      case 'user': return '👤';
      case 'superadmin': return '🛡️';
      case 'subscription': return '💳';
      default: return '📋';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
          <p className="text-gray-500">All superadmin actions are logged here</p>
        </div>
        <button
          onClick={() => fetchLogs(0)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading audit log: {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <FiRefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      )}

      {/* Audit Log Table */}
      {!loading && !error && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admin</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confirm Level</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(log.performed_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{log.superadmin_email}</p>
                        <p className="text-xs text-gray-500">{log.superadmin_role}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getActionColor(log.action)}`}>
                        {log.action || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="flex items-center gap-1">
                        {getCategoryIcon(log.action_category)}
                        {log.action_category || 'system'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {log.target_type && (
                        <span>
                          {log.target_type}: {log.target_id?.slice(0, 8)}...
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {log.confirmation_level === 2 ? (
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                          2-Level
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                          Standard
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No audit log entries found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={() => fetchLogs(page - 1)}
              disabled={page === 0 || loading}
              className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <span className="text-sm text-gray-500">Page {page + 1}</span>
            <button
              onClick={() => fetchLogs(page + 1)}
              disabled={!hasMore || loading}
              className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLog;
